import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import React, { useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import { updateStatusForTimeline } from "../../../api/timelineService";
import { updateUserDetails } from "../../../api/userService";
import useCustomToast from "../../../customHooks/useCustomToast";
import { setUserDetails, setUserTimelineStep } from "../../../Redux/userSlice";
import { isValidEmail } from "../../../utils/UTILITY_METHODS";

const PersonalInformation = () => {
  const { firstName, lastName, email, phone } = useSelector(
    (store) => store.userDetails
  );

  const [userData, setUserData] = useState({
    firstName,
    lastName,
    userEmail: email,
    userPhone: phone,
  });

  const dispatch = useDispatch();

  const [originalUserData, setOriginalUserData] = useState({
    firstName,
    lastName,
    userEmail: email,
    userPhone: phone,
  }); // To store original data for comparison
  const showToast = useCustomToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !userData.firstName.trim() ||
      !userData.lastName.trim() ||
      !userData.userEmail.trim() ||
      !userData.userPhone.trim() ||
      userData.userPhone.length < 10
    ) {
      showToast("All fields are required", "error");
      return;
    }

    if (!isValidEmail(userData.userEmail)) {
      showToast("Please enter valid email id", "error");
      return;
    }

    if (userData.userPhone.length !== 10) {
      showToast("Please enter valid phone number", "error");
      return;
    }

    const { firstName, lastName, userEmail, userPhone } = userData;
    const fullName = firstName + " " + lastName;

    if (!fullName?.trim() || !userEmail?.trim() || !userPhone?.trim()) {
      showToast("All fields are required", "error");
      return;
    }

    // Check if user data was updated
    if (
      !(originalUserData.firstName === firstName) ||
      !(originalUserData.lastName === lastName) ||
      !(originalUserData.userEmail === userEmail) ||
      !(originalUserData.userPhone === userPhone)
    ) {
      // If updated, call the update API
      updateUser(fullName, userEmail);

      //update data into redux
      dispatch(setUserDetails(userData));

      // update local state variables
      setUserData(userData);
      setOriginalUserData(userData);

      //update status timeline
      updateStatusForTimeline(
        1,
        "personal_information",
        "personal_details",
        "completed"
      );
    } else {
      // If not updated, just go to the next tab
      dispatch(setUserTimelineStep(2));
    }
  };

  const updateUser = async (fullName, email) => {
    try {
      const response = await updateUserDetails(
        fullName.toLowerCase(),
        email.toLowerCase()
      );
      showToast(response[0].data, "success");
      dispatch(setUserTimelineStep(2));
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const handleKeyDown = (e, nextInputName) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextInputName) {
        const nextInput = document.querySelector(
          `input[name="${nextInputName}"]`
        );
        if (nextInput) {
          nextInput.focus();
        }
      } else {
        handleSubmit(e);
      }
    }
  };

  return (
    <Box>
      <Heading as={"h2"} fontSize={"22px"} lineHeight={"22px"}>
        Fill in your details
      </Heading>

      <form onSubmit={handleSubmit}>
        <HStack spacing={8} my={8}>
          <FormControl>
            <FormLabel fontSize={"14px"} fontWeight={400} htmlFor="firstName">
              First Name
            </FormLabel>
            <Input
              name="firstName"
              id="firstName"
              value={userData.firstName}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "lastName")}
              placeholder="Enter First Name"
              focusBorderColor="teal.500"
              py={"3"}
              className="capitalize"
              fontSize={"14px"}
              backgroundColor={"white"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={"14px"} fontWeight={400} htmlFor="lastName">
              Last Name
            </FormLabel>
            <Input
              name="lastName"
              id="lastName"
              value={userData.lastName}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "userEmail")}
              placeholder="Enter Last Name"
              focusBorderColor="teal.500"
              py={"3"}
              className="capitalize"
              fontSize={"14px"}
              backgroundColor={"white"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>
        </HStack>

        <HStack spacing={8} mb={6}>
          <FormControl>
            <FormLabel fontSize={"14px"} fontWeight={400} htmlFor="userEmail">
              Email
            </FormLabel>
            <Input
              name="userEmail"
              id="userEmail"
              type="email"
              value={userData.userEmail}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "userPhone")}
              placeholder="Enter Email Address"
              focusBorderColor="teal.500"
              py={"3"}
              fontSize={"14px"}
              backgroundColor={"white"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={"14px"} fontWeight={400} htmlFor="userPhone">
              Phone
            </FormLabel>
            <InputGroup>
              <InputLeftElement
                name="userPhone"
                pointerEvents="none"
                display={"flex"}
                alignItems={"center"}
                mt={"5px"}
              >
                <ReactCountryFlag
                  countryCode="IN" // Default to India, change as needed
                  svg
                  style={{
                    width: "20px",
                    height: "20px",
                    marginLeft: "2rem",
                  }}
                />
                <Text fontSize={"sm"} marginLeft={"5px"}>
                  +91
                </Text>
              </InputLeftElement>

              <Input
                name="userPhone"
                id="userPhone"
                type="tel" // Use 'tel' for better mobile input experience
                value={userData.userPhone}
                onChange={handleChange}
                onKeyDown={(e) => handleKeyDown(e, null)}
                placeholder="Enter Phone Number"
                focusBorderColor="teal.500"
                pl="4.1rem"
                py={"3"}
                maxLength={10}
                fontSize={"14px"}
                backgroundColor={"white"}
                height={"50px"}
                width={"288px"}
                borderRadius={"8px"}
                borderColor={"#E8E8E8"}
              />
            </InputGroup>
          </FormControl>
        </HStack>

        <Button
          type="submit"
          colorScheme={"teal"}
          width="7.5rem"
          size={"sm"}
          variant={"solid"}
          fontWeight={"semibold"}
          marginTop={1}
        >
          Next
        </Button>
      </form>
    </Box>
  );
};

export default PersonalInformation;
